import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, UserPlus, LogIn, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, site } = useAuth();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/${mode}`, { username: username.trim(), password });
      login(data);
      toast.success(mode === "login" ? `Tekrar hoş geldin ${data.user.username}!` : `Aramıza hoş geldin ${data.user.username}!`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const title = site?.site_title || "COREXE INFO PUGLIC";

  return (
    <div className="min-h-screen bg-[#05070D] cx-grid-bg relative overflow-hidden flex flex-col">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-blue-600/10 blur-[100px]" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-5xl grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <section className="hidden lg:block cx-fade-up">
            <p className="font-mono-cx text-xs uppercase tracking-[0.35em] text-cyan-400 mb-6">// erişim kapısı</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 leading-[1.05]">
              {title.split(" ").slice(0, 1)} <span className="cx-neon-text">{title.split(" ").slice(1).join(" ")}</span>
            </h1>
            <p className="mt-6 text-slate-400 max-w-md text-base leading-relaxed">
              Bu alana erişmek için giriş yapman ya da kayıt olman gerekiyor. Özel bilgi paketleri, canlı topluluk sohbeti ve daha fazlası içeride.
            </p>
            <ul className="mt-10 space-y-4 text-sm text-slate-300">
              <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-cyan-400" /> Güvenli oturum, IP korumalı sistem</li>
              <li className="flex items-center gap-3"><Lock className="h-4 w-4 text-cyan-400" /> Satın alınan içerikler sadece sana açılır</li>
              <li className="flex items-center gap-3"><UserPlus className="h-4 w-4 text-cyan-400" /> Kayıt olan herkes toplulukla sohbet edebilir</li>
            </ul>
          </section>

          <section className="cx-glass rounded-2xl p-6 sm:p-8 cx-fade-up" style={{ animationDelay: "120ms" }} data-testid="auth-card">
            <div className="lg:hidden mb-6">
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-50">{title}</h1>
              <p className="text-slate-400 text-sm mt-2">Devam etmek için giriş yap veya kayıt ol.</p>
            </div>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-950/70 border border-slate-800 mb-6">
              <button
                type="button"
                data-testid="auth-tab-login"
                onClick={() => { setMode("login"); setError(""); }}
                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-slate-800 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}
              >
                Giriş Yap
              </button>
              <button
                type="button"
                data-testid="auth-tab-register"
                onClick={() => { setMode("register"); setError(""); }}
                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-slate-800 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}
              >
                Kayıt Ol
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-mono-cx uppercase tracking-widest text-slate-400">Kullanıcı Adı</label>
                <Input data-testid="auth-username-input" className="cx-input mt-1.5" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="kullanici_adi" autoComplete="username" required />
              </div>
              <div>
                <label className="text-xs font-mono-cx uppercase tracking-widest text-slate-400">Şifre</label>
                <Input data-testid="auth-password-input" type="password" className="cx-input mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete={mode === "login" ? "current-password" : "new-password"} required />
              </div>
              {error && <p data-testid="auth-error" className="text-sm text-red-400 bg-red-950/40 border border-red-500/40 rounded-lg px-3 py-2">{error}</p>}
              <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-xl cx-btn-primary text-sm">
                {loading ? "Bekleyin..." : mode === "login" ? (<><LogIn className="h-4 w-4 mr-2" /> Giriş Yap</>) : (<><UserPlus className="h-4 w-4 mr-2" /> Kayıt Ol</>)}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-5 text-center">
              {mode === "login" ? "Hesabın yok mu? " : "Zaten hesabın var mı? "}
              <button type="button" className="text-cyan-400 hover:underline" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-switch-mode">
                {mode === "login" ? "Kayıt ol" : "Giriş yap"}
              </button>
            </p>
          </section>
        </div>
      </main>

      <footer className="relative z-10 py-6 text-center">
        <Link to="/admin/login" data-testid="auth-admin-login-link" className="text-xs text-slate-600 hover:text-slate-400 transition-colors inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Admin Girişi
        </Link>
      </footer>
    </div>
  );
}
